import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { FileText, Calendar } from 'lucide-react';
import { useAuth } from '@contexts/AuthContext';
import { formatGivenName } from '@utils/stringUtils';
import { formatMediumDateTime } from '@utils/dateUtils';
import PageLayout from '@components/layout/PageLayout';
import { ContractRoleMetrics, ContractRoleQuickActions, StatusChart } from '@components/features/dashboard';
import Button from '@components/common/Button';
import DashboardHeader from '@components/common/DashboardHeader';
import DashboardGrid from '@components/common/DashboardGrid';
import DashboardRow from '@components/common/DashboardRow';
import DashboardContainer from '@components/common/DashboardContainer';
import DashboardCard from '@components/common/DashboardCard';
import DashboardCol from '@components/common/DashboardCol';
import MetricsGrid from '@components/common/MetricsGrid';
import MetricCard from '@components/common/MetricCard';
import Card from '@components/common/Card';
import ErrorState from '@components/common/ErrorState';
import { assessmentService, calendarMeetingsService, contractService, dashboardService } from '@services';
import type { ContractRoleMetricsData, ContractRoleQuickActionsData } from '@app-types';
import styles from './ContractRoleDashboardPage.module.css';

const ContractRoleDashboardPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const contractsQuery = useQuery({
    queryKey: ['contract-role-contracts', user?.id],
    queryFn: () => contractService.getContracts({ limit: 5, offset: 0 }),
    enabled: !!user,
    refetchInterval: 60000
  });

  const assessmentsQuery = useQuery({
    queryKey: ['contract-role-assessments', user?.id],
    queryFn: () => assessmentService.getAssessments({ limit: 50, offset: 0 }),
    enabled: !!user,
    refetchInterval: 60000
  });

  // Each section fails on its own (D11): a failed request must not take down the rest of the page.
  const metricsQuery = useQuery<ContractRoleMetricsData>({
    queryKey: ['contract-role-metrics', user?.id],
    queryFn: () => dashboardService.getContractRoleMetrics(),
    enabled: !!user,
    // The app default is not to refetch a cached query on mount; counts must be fresh on every page load
    refetchOnMount: 'always',
    refetchInterval: 60000
  });

  const quickActionsQuery = useQuery<ContractRoleQuickActionsData>({
    queryKey: ['contract-role-quick-actions', user?.id],
    queryFn: () => dashboardService.getContractRoleQuickActions(),
    enabled: !!user,
    // The app default is not to refetch a cached query on mount; counts must be fresh on every page load
    refetchOnMount: 'always',
    refetchInterval: 60000
  });

  const meetingsQuery = useQuery({
    queryKey: ['contract-role-meetings', user?.id],
    queryFn: () => {
      const start = new Date();
      const end = new Date();
      end.setDate(end.getDate() + 60);
      return calendarMeetingsService.getCalendarEvents({
        startDate: start.toISOString(),
        endDate: end.toISOString()
      });
    },
    enabled: !!user,
    refetchInterval: 60000
  });

  const error = contractsQuery.error || assessmentsQuery.error || meetingsQuery.error;
  const contracts = contractsQuery.data?.data ?? [];
  const assessments = assessmentsQuery.data?.data ?? [];
  const upcomingMeetings = (meetingsQuery.data?.events ?? [])
    .filter((event) => event.status === 'scheduled' || event.status === 'rescheduled')
    .slice(0, 5);

  const skippedStatusKeys = new Set(['all', 'conflicts', 'conflictsTotal']);
  const totals = assessmentsQuery.data?.statusTotals;
  const statusFromTotals = totals
    ? Object.entries(totals)
        .filter((entry): entry is [string, number] => {
          const [status, count] = entry;
          return !skippedStatusKeys.has(status) && typeof count === 'number' && count > 0;
        })
        .map(([status, count]) => ({ status, count }))
    : [];
  const statusCounts =
    statusFromTotals.length > 0
      ? statusFromTotals
      : assessments.reduce<Array<{ status: string; count: number }>>((acc, assessment) => {
          const status = assessment.assessment_status || 'Unknown';
          const existing = acc.find((item) => item.status === status);
          if (existing) {
            existing.count += 1;
          } else {
            acc.push({ status, count: 1 });
          }
          return acc;
        }, []);

  const isLoading = contractsQuery.isLoading || assessmentsQuery.isLoading || meetingsQuery.isLoading;

  if (error) {
    return (
      <PageLayout>
        <ErrorState
          title="Dashboard Unavailable"
          message="Failed to load Contract Role dashboard data."
          action={
            <Button variant="primary" onClick={() => window.location.reload()}>
              Refresh Page
            </Button>
          }
        />
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <DashboardContainer data-test-id="contract-role-dashboard">
        <DashboardHeader
          title={`Welcome back, ${user ? formatGivenName(user) : ''}!`}
          subtitle="Your assigned contracts, evidence, meetings, and assessment status"
          role="Logged in as Contract Role"
          lastUpdated={`Last updated: ${new Date().toLocaleTimeString()}`}
        />

        <DashboardGrid>
          <DashboardRow>
            <MetricsGrid>
              <MetricCard
                icon={<FileText size={24} />}
                label="Assigned Contracts"
                value={contractsQuery.data?.total ?? 0}
                subtitle="Contracts assigned to you"
                variant="blue"
                loading={isLoading}
              />
              <MetricCard
                icon={<Calendar size={24} />}
                label="Upcoming Meetings"
                value={upcomingMeetings.length}
                subtitle="Next 60 days"
                variant="purple"
                loading={isLoading}
              />
            </MetricsGrid>
          </DashboardRow>

          <DashboardRow>
            <ContractRoleMetrics
              data={metricsQuery.data}
              isLoading={metricsQuery.isLoading}
              isError={metricsQuery.isError}
              onRetry={() => void metricsQuery.refetch()}
            />
          </DashboardRow>

          <DashboardRow variant="2col">
            <DashboardCol>
              <Card variant="primary" className={styles.dashboardCard}>
                <Card.Header>
                  <h3>
                    <FileText size={18} />
                    Assigned Contracts
                  </h3>
                  <Button variant="ghost" size="sm" onClick={() => navigate('/contracts')}>
                    View All
                  </Button>
                </Card.Header>
                <Card.Content>
                  {contracts.length === 0 ? (
                    <p className={styles.emptyState}>No assigned contracts.</p>
                  ) : (
                    <ul className={styles.list}>
                      {contracts.map((contract) => (
                        <li key={contract.id}>
                          <button
                            type="button"
                            className={styles.listItem}
                            onClick={() => navigate(`/contracts/${contract.id}`)}
                          >
                            <span className={styles.listItemTitle}>{contract.contract_number}</span>
                            {contract.name && contract.name !== contract.contract_number && (
                              <span className={styles.listItemSubtitle}>{contract.name}</span>
                            )}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </Card.Content>
              </Card>
            </DashboardCol>

            <DashboardCol>
              <Card variant="secondary" className={styles.dashboardCard}>
                <Card.Header>
                  <h3>
                    <Calendar size={18} />
                    Upcoming Meetings
                  </h3>
                </Card.Header>
                <Card.Content>
                  {upcomingMeetings.length === 0 ? (
                    <p className={styles.emptyState}>No upcoming meetings.</p>
                  ) : (
                    <ul className={styles.list}>
                      {upcomingMeetings.map((event) => (
                        <li key={event.id}>
                          <button
                            type="button"
                            className={styles.listItem}
                            onClick={() => navigate(`/contract-assessment-meetings/${event.id}`)}
                          >
                            <span className={styles.listItemTitle}>
                              {event.contracts[0]?.contract_number ?? 'Meeting'}
                            </span>
                            <span className={styles.listItemSubtitle}>
                              {formatMediumDateTime(event.scheduled_datetime)}
                            </span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </Card.Content>
              </Card>
            </DashboardCol>
          </DashboardRow>

          <DashboardRow>
            <DashboardCard>
              <StatusChart data={statusCounts} isLoading={isLoading} dataTestId="contract-role-status-chart" />
            </DashboardCard>
          </DashboardRow>

          <DashboardRow>
            <DashboardCard>
              <ContractRoleQuickActions
                data={quickActionsQuery.data}
                isLoading={quickActionsQuery.isLoading}
                isError={quickActionsQuery.isError}
                onRetry={() => void quickActionsQuery.refetch()}
              />
            </DashboardCard>
          </DashboardRow>
        </DashboardGrid>
      </DashboardContainer>
    </PageLayout>
  );
};

export default ContractRoleDashboardPage;
